import VisitaAPI from 'dashboard/api/visitas';
import { createStore } from 'dashboard/store/storeFactory';
import camelcaseKeys from 'camelcase-keys';
import { getVisitaCardStatus } from 'dashboard/helper/lais/visitaPresenter';

const normalizeVisitas = records =>
  camelcaseKeys(records || [], { deep: true });

const dayKeyFor = visita => {
  const value = visita?.scheduledAt || visita?.scheduled_at;
  if (!value) return 'sem_data';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'sem_data';
  return date.toISOString().slice(0, 10);
};

export const useVisitasStore = createStore({
  name: 'visitas',
  type: 'pinia',
  API: VisitaAPI,
  state: () => ({
    actionFlags: {},
  }),
  getters: {
    getVisitasList: state => normalizeVisitas(state.records),
    getVisitaActionFlags: state => state.actionFlags || {},
    getVisitaActionFlag: state => id => state.actionFlags?.[id] || {},
    getVisitasByDay: state =>
      normalizeVisitas(state.records).reduce((days, visita) => {
        const key = dayKeyFor(visita);
        days[key] = days[key] || [];
        days[key].push({
          ...visita,
          cardStatus: getVisitaCardStatus(visita),
        });
        return days;
      }, {}),
  },
  actions: () => ({
    setVisitaActionFlag(id, patch = {}) {
      if (!id) return;
      this.actionFlags = {
        ...this.actionFlags,
        [id]: {
          ...(this.actionFlags?.[id] || {}),
          ...patch,
        },
      };
    },
    clearVisitaActionFlag(id) {
      if (!id || !this.actionFlags?.[id]) return;
      const nextFlags = { ...this.actionFlags };
      delete nextFlags[id];
      this.actionFlags = nextFlags;
    },
    async withVisitaActionFlag(id, action, callback) {
      this.setVisitaActionFlag(id, {
        action,
        loading: true,
        error: null,
        done: false,
      });

      try {
        const result = await callback();
        this.setVisitaActionFlag(id, {
          loading: false,
          done: true,
          error: null,
        });
        return result;
      } catch (error) {
        this.setVisitaActionFlag(id, {
          loading: false,
          done: false,
          error,
        });
        throw error;
      }
    },
    async show(id) {
      this.setUIFlag({ fetchingItem: true });
      try {
        const { data } = await VisitaAPI.show(id);
        const record = camelcaseKeys(data.payload || data, { deep: true });
        const index = this.records.findIndex(
          item => String(item.id) === String(record.id)
        );
        if (index >= 0) {
          this.records[index] = record;
        } else {
          this.records.push(record);
        }
        return record;
      } finally {
        this.setUIFlag({ fetchingItem: false });
      }
    },
    async availability(params = {}) {
      const { data } = await VisitaAPI.availability(params);
      return data.payload || data;
    },
    async agenda(params = {}) {
      const { data } = await VisitaAPI.agenda(params);
      return camelcaseKeys(data.payload || data, { deep: true });
    },
    async recommendations(id, params = {}) {
      const { data } = await VisitaAPI.recommendations(id, params);
      return camelcaseKeys(data.payload || data, { deep: true });
    },
    async submitReviewReport(id, payload = {}) {
      const { data } = await VisitaAPI.submitReviewReport(id, payload);
      return camelcaseKeys(data.payload || data, { deep: true });
    },
    async finish(id, payload = {}) {
      const { data } = await VisitaAPI.finish(id, payload);
      return camelcaseKeys(data.payload || data, { deep: true });
    },
    async submitRequestFeedback(id, payload = {}) {
      const { data } = await VisitaAPI.submitRequestFeedback(id, payload);
      return camelcaseKeys(data.payload || data, { deep: true });
    },
    async reschedule(id, payload = {}) {
      const { data } = await VisitaAPI.reschedule(id, payload);
      return camelcaseKeys(data.payload || data, { deep: true });
    },
    async updateAvailability(payload = {}) {
      const { data } = await VisitaAPI.updateAvailability(payload);
      return data.payload || data;
    },
    async agentScheduleBlocks(params = {}) {
      const { data } = await VisitaAPI.agentScheduleBlocks(params);
      return camelcaseKeys(data.payload || data, { deep: true });
    },
    async agentScheduleBlockConfig(params = {}) {
      const { data } = await VisitaAPI.agentScheduleBlockConfig(params);
      return camelcaseKeys(data.payload || data, { deep: true });
    },
    async createAgentScheduleBlock(payload = {}) {
      const { data } = await VisitaAPI.createAgentScheduleBlock(payload);
      return camelcaseKeys(data.payload || data, { deep: true });
    },
    async bulkCreateAgentScheduleBlocks(payload = {}) {
      const { data } = await VisitaAPI.bulkCreateAgentScheduleBlocks(payload);
      return camelcaseKeys(data, { deep: true });
    },
    async updateAgentScheduleBlock(id, payload = {}) {
      const { data } = await VisitaAPI.updateAgentScheduleBlock(id, payload);
      return camelcaseKeys(data.payload || data, { deep: true });
    },
    async deleteAgentScheduleBlock(id) {
      await VisitaAPI.deleteAgentScheduleBlock(id);
    },
    async agentKeys(params = {}) {
      const { data } = await VisitaAPI.agentKeys(params);
      return camelcaseKeys(data.payload || data, { deep: true });
    },
    async createKeyHandover(imovelId, payload = {}, options = {}) {
      const { data } = await VisitaAPI.createKeyHandover(
        imovelId,
        payload,
        options
      );
      return camelcaseKeys(data.payload || data, { deep: true });
    },
    async pickupKeyHandover(id, payload = {}) {
      const { data } = await VisitaAPI.pickupKeyHandover(id, payload);
      return camelcaseKeys(data.payload || data, { deep: true });
    },
    async notDeliveredKeyHandover(id, payload = {}) {
      const { data } = await VisitaAPI.notDeliveredKeyHandover(id, payload);
      return camelcaseKeys(data.payload || data, { deep: true });
    },
    async returnKeyHandover(id) {
      const { data } = await VisitaAPI.returnKeyHandover(id);
      return camelcaseKeys(data.payload || data, { deep: true });
    },
    async batchCancel(ids = [], payload = {}) {
      const { data } = await VisitaAPI.batchCancel(ids, payload);
      return camelcaseKeys(data.payload || data, { deep: true });
    },
  }),
});
